import React, { useEffect, useState } from "react"
import { Helmet } from 'react-helmet-async';
import { Container } from "@mui/material";
import axios from "axios";
import Swal from 'sweetalert2';

import { Url, awsFileUrl } from "../url/url";
import "../App.css"

const resolveMedia = (path) => {
    if (!path) return null;
    return path.startsWith("http") ? path : `${awsFileUrl}/${path}`;
};

const emptyForm = { title: "", description: "", videoUrl: "", thumbnailImage: "" };

const WebVideo = () => {
    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);

    const [showForm, setShowForm] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [formValue, setFormValue] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    // Video upload
    const [videoUploadLoading, setVideoUploadLoading] = useState(false);
    const [videoProgress, setVideoProgress] = useState(0);

    // Optional thumbnail upload
    const [thumbUploadLoading, setThumbUploadLoading] = useState(false);
    const [thumbProgress, setThumbProgress] = useState(0);

    // ---------------------------------------------
    // Fetch all videos (admin sees active + inactive both)
    // ---------------------------------------------
    const fetchVideos = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${Url}/api/web-video`);
            setVideos(res.data?.data || []);
        } catch (error) {
            console.error("Error fetching videos:", error);
            Swal.fire("Could not load videos. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVideos();
    }, []);

    const formHandle = (e) => {
        const { name, value } = e.target;
        setFormValue({ ...formValue, [name]: value });
    };

    // ---------------------------------------------
    // Video file upload — presigned URL → S3 PUT (same pattern as thumbnail uploads)
    // ---------------------------------------------
    const videoHandle = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setVideoUploadLoading(true);
        setVideoProgress(0);

        try {
            const response = await axios.post(`${Url}/api/createPresignedUrl/products`, {
                fileName: file.name,
                fileType: file.type,
            });

            if (response.status === 200) {
                const { preSignedUrl, key } = response?.data;
                const uploadRes = await axios.put(preSignedUrl, file, {
                    headers: { "Content-Type": file.type },
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setVideoProgress(progress);
                    },
                });

                if (uploadRes.status === 200) {
                    const fileUrl = `${awsFileUrl}/${key}`;
                    setFormValue((prev) => ({ ...prev, videoUrl: fileUrl }));
                } else {
                    Swal.fire("Video upload failed, please try again!");
                }
            }
        } catch (error) {
            console.log(error);
            Swal.fire("Video upload failed, please try again!");
        } finally {
            setVideoUploadLoading(false);
            setVideoProgress(0);
        }
    };

    // ---------------------------------------------
    // Optional thumbnail/poster image upload
    // ---------------------------------------------
    const thumbnailHandle = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setThumbUploadLoading(true);
        setThumbProgress(0);

        try {
            const response = await axios.post(`${Url}/api/createPresignedUrl/products`, {
                fileName: file.name,
                fileType: file.type,
            });

            if (response.status === 200) {
                const { preSignedUrl, key } = response?.data;
                const uploadRes = await axios.put(preSignedUrl, file, {
                    headers: { "Content-Type": file.type },
                    onUploadProgress: (progressEvent) => {
                        const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                        setThumbProgress(progress);
                    },
                });

                if (uploadRes.status === 200) {
                    const fileUrl = `${awsFileUrl}/${key}`;
                    setFormValue((prev) => ({ ...prev, thumbnailImage: fileUrl }));
                } else {
                    Swal.fire("Thumbnail upload failed, please try again!");
                }
            }
        } catch (error) {
            console.log(error);
            Swal.fire("Thumbnail upload failed, please try again!");
        } finally {
            setThumbUploadLoading(false);
            setThumbProgress(0);
        }
    };

    const openAddForm = () => {
        setEditingId(null);
        setFormValue(emptyForm);
        setShowForm(true);
    };

    const openEditForm = (video) => {
        setEditingId(video._id);
        setFormValue({
            title: video.title || "",
            description: video.description || "",
            videoUrl: video.videoUrl || "",
            thumbnailImage: video.thumbnailImage || "",
        });
        setShowForm(true);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditingId(null);
        setFormValue(emptyForm);
    };

    // ---------------------------------------------
    // Create / Update
    // ---------------------------------------------
    const submit = async (e) => {
        e.preventDefault();

        if (!formValue.title) {
            Swal.fire("Please enter a title!");
            return;
        }
        if (videoUploadLoading) {
            Swal.fire("Video abhi upload ho raha hai, thoda ruk jao!");
            return;
        }
        if (!formValue.videoUrl) {
            Swal.fire("Please upload a video!");
            return;
        }
        if (thumbUploadLoading) {
            Swal.fire("Thumbnail abhi upload ho raha hai, thoda ruk jao!");
            return;
        }

        setSaving(true);
        try {
            if (editingId) {
                await axios.put(`${Url}/api/web-video/${editingId}`, formValue, {
                    headers: { 'Content-Type': 'application/json' },
                });
                Swal.fire({
                    position: "top-end",
                    icon: "success",
                    title: "Video Updated Successfully",
                    showConfirmButton: false,
                    timer: 1500,
                });
            } else {
                await axios.post(`${Url}/api/web-video`, formValue, {
                    headers: { 'Content-Type': 'application/json' },
                });
                Swal.fire({
                    position: "top-end",
                    icon: "success",
                    title: "Video Added Successfully",
                    showConfirmButton: false,
                    timer: 1500,
                });
            }

            closeForm();
            fetchVideos();
        } catch (error) {
            console.error("SAVE VIDEO ERROR:", error);
            Swal.fire({
                position: "top-end",
                icon: "error",
                title: error?.response?.data?.message || "Something Went Wrong",
                showConfirmButton: false,
                timer: 1500,
            });
        } finally {
            setSaving(false);
        }
    };

    // ---------------------------------------------
    // Active / Inactive toggle
    // ---------------------------------------------
    const toggleStatus = async (video) => {
        try {
            setVideos((prev) =>
                prev.map((v) => (v._id === video._id ? { ...v, isActive: !v.isActive } : v))
            );
            await axios.patch(`${Url}/api/web-video/${video._id}/toggle-status`);
        } catch (error) {
            console.error("TOGGLE STATUS ERROR:", error);
            Swal.fire("Could not change status, please try again.");
            fetchVideos(); // revert on failure
        }
    };

    // ---------------------------------------------
    // Delete
    // ---------------------------------------------
    const handleDelete = async (video) => {
        const confirm = await Swal.fire({
            title: `Delete "${video.title}"?`,
            text: "This cannot be undone.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, delete it",
        });

        if (!confirm.isConfirmed) return;

        try {
            await axios.delete(`${Url}/api/web-video/${video._id}`);
            Swal.fire({
                position: "top-end",
                icon: "success",
                title: "Video Deleted",
                showConfirmButton: false,
                timer: 1200,
            });
            setVideos((prev) => prev.filter((v) => v._id !== video._id));
        } catch (error) {
            console.error("DELETE VIDEO ERROR:", error);
            Swal.fire("Could not delete video, please try again.");
        }
    };

    return (
        <>
            <Helmet>
                <title> Dashboard: Web Videos </title>
            </Helmet>

            <Container>
                <div className="mb-4 text-primary d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div>
                        <h2 className="text-capitalize">Website Videos</h2>
                        <p className="text-secondary mb-0" style={{ fontSize: 14 }}>
                            Upload, edit, delete, and show/hide videos used on the website.
                        </p>
                    </div>
                    {!showForm && (
                        <button className="btn btn-primary" onClick={openAddForm}>
                            + Add Video
                        </button>
                    )}
                </div>

                {/* ===== Add / Edit form ===== */}
                {showForm && (
                    <div className="p-3 bg-light rounded shadow border mb-4">
                        <form onSubmit={submit}>
                            <div className="row">
                                <div className="col-lg-6 col-12 mb-2">
                                    <div className="form-group">
                                        <p className="mb-0 fw-bold">Title</p>
                                        <input
                                            type="text"
                                            name="title"
                                            value={formValue.title}
                                            onChange={formHandle}
                                            className="form-control"
                                            placeholder="e.g. How to bathe your puppy"
                                            required
                                        />
                                    </div>
                                </div>

                                <div className="col-lg-6 col-12 mb-2">
                                    <div className="form-group">
                                        <p className="mb-0 fw-bold">
                                            Description <span className="fw-normal text-secondary">(optional)</span>
                                        </p>
                                        <input
                                            type="text"
                                            name="description"
                                            value={formValue.description}
                                            onChange={formHandle}
                                            className="form-control"
                                        />
                                    </div>
                                </div>

                                <div className="col-lg-6 col-12 mb-2">
                                    <div className="form-group">
                                        <p className="mb-0 fw-bold">Video File</p>
                                        <input
                                            type="file"
                                            accept="video/*"
                                            onChange={videoHandle}
                                            className="form-control"
                                        />
                                        {videoUploadLoading && (
                                            <div className="d-flex align-items-center gap-2 pt-2">
                                                <progress value={videoProgress} max="100" style={{ height: 5, flex: 1 }} />
                                                <small className="text-secondary">{videoProgress}%</small>
                                            </div>
                                        )}
                                        {!videoUploadLoading && formValue.videoUrl && (
                                            <div className="pt-2">
                                                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                                                <video
                                                    src={resolveMedia(formValue.videoUrl)}
                                                    controls
                                                    style={{ width: "100%", maxWidth: 260, borderRadius: 8 }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="col-lg-6 col-12 mb-2">
                                    <div className="form-group">
                                        <p className="mb-0 fw-bold">
                                            Thumbnail / Poster <span className="fw-normal text-secondary">(optional)</span>
                                        </p>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={thumbnailHandle}
                                            className="form-control"
                                        />
                                        {thumbUploadLoading && (
                                            <div className="d-flex align-items-center gap-2 pt-2">
                                                <progress value={thumbProgress} max="100" style={{ height: 5, flex: 1 }} />
                                                <small className="text-secondary">{thumbProgress}%</small>
                                            </div>
                                        )}
                                        {!thumbUploadLoading && formValue.thumbnailImage && (
                                            <img
                                                src={resolveMedia(formValue.thumbnailImage)}
                                                alt="thumbnail"
                                                style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, marginTop: 8 }}
                                            />
                                        )}
                                    </div>
                                </div>

                                <div className="col-12 d-flex justify-content-end gap-2 mt-2">
                                    <button type="button" className="btn btn-outline-secondary" onClick={closeForm}>
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={saving || videoUploadLoading || thumbUploadLoading}
                                    >
                                        {saving ? "Saving..." : editingId ? "Update Video" : "Post Video"}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {/* ===== Video list ===== */}
                {loading ? (
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" />
                    </div>
                ) : videos.length === 0 ? (
                    <div className="text-center text-secondary py-5">No videos added yet.</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table align-middle">
                            <thead>
                                <tr>
                                    <th>Preview</th>
                                    <th>Title</th>
                                    <th>Status</th>
                                    <th className="text-end">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {videos.map((video) => (
                                    <tr key={video._id}>
                                        <td style={{ width: 160 }}>
                                            {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                                            <video
                                                src={resolveMedia(video.videoUrl)}
                                                poster={video.thumbnailImage ? resolveMedia(video.thumbnailImage) : undefined}
                                                controls
                                                style={{ width: 140, borderRadius: 8 }}
                                            />
                                        </td>
                                        <td>
                                            <div className="fw-bold">{video.title}</div>
                                            {video.description && (
                                                <div className="text-secondary" style={{ fontSize: 13 }}>
                                                    {video.description}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <div className="form-check form-switch">
                                                <input
                                                    type="checkbox"
                                                    className="form-check-input"
                                                    id={`status-${video._id}`}
                                                    checked={video.isActive}
                                                    onChange={() => toggleStatus(video)}
                                                />
                                                <label htmlFor={`status-${video._id}`} className="form-check-label">
                                                    {video.isActive ? "Active" : "Inactive"}
                                                </label>
                                            </div>
                                        </td>
                                        <td className="text-end">
                                            <button
                                                className="btn btn-sm btn-outline-primary me-2"
                                                onClick={() => openEditForm(video)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                className="btn btn-sm btn-outline-danger"
                                                onClick={() => handleDelete(video)}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Container>
        </>
    )
}

export default WebVideo